/**
 * SocketCommand.js
 * Socket + Feeder ekleme/silme komutları
 *
 * Socket eklenirken:
 * 1. Component (socket) oluşturulur
 * 2. Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine socket ve wire eklenir
 */

import BaseWiringCommand from './BaseWiringCommand';
import * as THREE from 'three';
import { createSingleSocket, singleCircleBuat, singleSquareBuat } from '../../../factory/symbols/createSocketSymbols';
import { removeFeeder, removeComponent } from '../../../redux/simpleSupplySlice';
import { deleteWire } from '../../../redux/wireSlice';
import { addTesisat, deleteTesisat } from '../../../redux/tesisatSlice';
import { addTesisatIdToFloorPlan, removeTesisatIdFromFloorPlan } from '../../../redux/floorPlanSlice';

class SocketCommand extends BaseWiringCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} socketData - Socket component verisi
   * @param {object} feederData - Feeder verisi
   * @param {object} sceneData - Three.js scene verisi (socket position, wire vertices, etc.)
   */
  constructor(action, socketData, feederData, sceneData) {
    // ✅ sceneData'dan renk al (REDO uyumluluğu)
    const chosenColor = (sceneData && sceneData.color) || (socketData && socketData.metadata && socketData.metadata.color) || 0x0045ff;

    super(
      action === 'ADD' ? 'ADD_SOCKET' : 'DELETE_SOCKET',
      action,
      socketData,
      feederData,
      sceneData,
      {
        componentType: 'socket',
        cacheMethodSuffix: 'Socket',
        wireColor: chosenColor, // ✅ sceneData'dan renk
        createWire: true
      }
    );
    // restore metadata (used to re-enter draw mode after undo)
    this.restoreVertices = (sceneData && (sceneData.restoreVertices || sceneData.wireVertices)) || [];
    this.restoreSource = (sceneData && sceneData.restoreSource) || null;
    this.restoreCommandType = (sceneData && sceneData.restoreCommandType) || null;
    // keep any provided restoreCallback (may be passed via options or sceneData)
    this.restoreCallback = (this.options && this.options.restoreCallback) || (sceneData && sceneData.restoreCallback) || null;
  }

  /**
   * Socket sembolünü oluştur
   * @override
   */
  createSymbol(scene, socketData, sceneData) {
    // ✅ DUPLICATE DETECTION: Aynı ID ile socket zaten varsa yeniden oluşturma
    const socketId = socketData?.id;
    if (socketId) {
      let existingSocket = null;
      scene.traverse(obj => {
        if (obj.userData?.id === socketId) {
          existingSocket = obj;
        }
      });
      if (existingSocket) {
         return existingSocket;
      }
    }

    // ✅ socketPosition yoksa socketInfos'tan al (REDO uyumluluğu)
    let socketPosition;
    if (sceneData.socketPosition) {
      socketPosition = new THREE.Vector3(
        sceneData.socketPosition[0],
        sceneData.socketPosition[1],
        sceneData.socketPosition[2]
      );
    } else if (sceneData.socketInfos && sceneData.socketInfos.length > 0) {
      // REDO: socketInfos array'inden pozisyon al
      const info = sceneData.socketInfos[0];
      socketPosition = new THREE.Vector3(
        info.position.x,
        info.position.y,
        info.position.z || 0
      );
    } else if (socketData?.position) {
      // Fallback: componentData'dan pozisyon al
      socketPosition = new THREE.Vector3(
        socketData.position.x,
        socketData.position.y,
        socketData.position.z || 0
      );
    } else {
      console.warn('[SocketCommand] createSymbol: socketPosition bulunamadı, atlanıyor');
      return null;
    }

    const dir = sceneData.dir || 1;
    const color = sceneData.color || 0x0045ff;
    const tempLineUUID = sceneData.tempLineUUID || null;

    // ✅ REDO uyumluluğu: socketInfos'tan type ve rotateValue al
    const infoItem = sceneData.socketInfos?.[0];
    const socketType = sceneData.socketType || infoItem?.type || 'kapaklı';
    const rotateValue = sceneData.rotateValue ?? infoItem?.rotateValue ?? 0;
    // ✅ stretchLength from sceneData or socketData metadata
    const stretchLength = sceneData.stretchLength ?? socketData?.metadata?.stretchLength ?? 0;
    // socketId zaten fonksiyon başında tanımlandı (duplicate detection için)

    const socketGroup = createSingleSocket(
      scene,
      socketPosition,
      dir,
      color,
      false, // isPreview
      socketType,
      tempLineUUID,
      rotateValue,
      socketId,      // ✅ providedSocketId parametresi
      stretchLength  // ✅ stretchLength parametresi
    );

    // Rotation'ı ayrıca set et
    socketGroup.rotation.z = sceneData.rotation || 0;

    return socketGroup;
  }

  execute(scene, dispatch, skipRedux = false) {
    super.execute(scene, dispatch, skipRedux);

    // 🆕 tesisatSlice'a socket meta'larını yaz (REDO için)
    if (!skipRedux && this.sceneData?.socketInfos?.length > 0) {
      const color = this.sceneData?.color || 0x0045ff;
      this.sceneData.socketInfos.forEach(info => {
        if (!info?.id) return;
        dispatch(addTesisat({
          id: info.id,
          type: 'socket',
          subtype: info.type || 'normal',
          position: info.position,
          rotation: info.rotation || 0,
          rotateValue: info.rotateValue || 0,
          stretchLength: info.stretchLength || 0,
          power: info.power || 300,
          color,
          floorPlanId: info.floorPlanId || null,
          dir: info.dir || 1,
          _v: 2, // position format: absolute world — useTesisatRebuild old-format branch'ini atla
        }));
        // Phase A.2: floor plan indexine yaz
        if (info.floorPlanId) {
          dispatch(addTesisatIdToFloorPlan({
            tabId: 'elektrik',
            floorPlanId: info.floorPlanId,
            itemId: info.id,
          }));
        }
      });
    }

    // ✅ Buatları yeniden oluştur (REDO için)
    // skipRedux = false ise global redo (Enter sonrası), buatları oluştur
    if (!skipRedux && this.sceneData?.buatInfos && this.sceneData.buatInfos.length > 0) {
 
      const color = this.sceneData?.color || 0x0045ff;

      this.sceneData.buatInfos.forEach(buatInfo => {
        // Bu buat zaten sahnede var mı kontrol et
        let existingBuat = null;
        scene.traverse(obj => {
          if (obj.userData?.id === buatInfo.id) {
            existingBuat = obj;
          }
        });

        if (existingBuat) {
           return;
        }

        const point = new THREE.Vector3(
          buatInfo.position.x,
          buatInfo.position.y,
          buatInfo.position.z || 0
        );

        let buat;
        if (buatInfo.buatType === 'square') {
          buat = singleSquareBuat(scene, point, color, buatInfo.id);
          // singleSquareBuat scene.add yapmıyor, sonra ekliyoruz
        } else {
          buat = singleCircleBuat(point, buatInfo.panelID, buatInfo.id, color);
          scene.add(buat);
        }

        // userData'yı geri yükle
        if (buat) {
          buat.userData = {
            ...buat.userData,
            id: buatInfo.id,
            type: buatInfo.type || 'socketBuat',
            buatType: buatInfo.buatType || 'circle',
            socketLoadId: buatInfo.socketLoadId,
            wireID: buatInfo.wireID,
            panelID: buatInfo.panelID,
            function: buatInfo.function || 'simpleSocket'
          };

        }
      });
    }

    // ✅ Wire'ı yeniden oluştur (REDO) — branch (ek) hatta commandFeederData=null
    // olduğu için super.execute createWire çalışmıyor (feederData yok), branch wire
    // kalıcı oluşmuyordu. sceneData'dan idempotent kur + addWire (rebuild/save için).
    if (!skipRedux && this.sceneData?.wireId && Array.isArray(this.sceneData?.vertices) && this.sceneData.vertices.length > 1) {
      const wid = this.sceneData.wireId;
      const widID = this.sceneData.wireID || wid;
      let exists = false;
      scene.traverse(o => {
        if (o.type === 'Line' && (o.userData?.feederId === wid || o.userData?.id === widID)) exists = true;
      });
      if (!exists) {
        const col = this.sceneData?.color || 0x0045ff;
        const pts = this.sceneData.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z || 0));
        const wireLine = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(pts),
          new THREE.LineBasicMaterial({ color: col })
        );
        wireLine.userData = {
          id: widID,
          feederId: wid,
          type: 'wireSocket',
          function: 'simpleSocket',
          panelID: this.sceneData.panelID || null,
          socketLoadId: this.sceneData.socketLoadId || null,
          wireID: this.sceneData.wireID || null,
          sockets: this.sceneData.socketIds || [],
          isSelectable: true,
        };
        scene.add(wireLine);
        try {
          const { addWire } = require('../../../redux/wireSlice');
          dispatch(addWire({
            id: wid,
            sceneUUID: wireLine.uuid,
            type: 'polyline',
            vertices: this.sceneData.vertices,
            layer: 'socket',
            color: '#' + (typeof col === 'number' ? col.toString(16).padStart(6, '0') : '0045ff'),
            userData: { ...wireLine.userData },
          }));
        } catch (e) { /* addWire redo */ }
      }
    }
  }

  undo(scene, dispatch, skipRedux = false) {
    

    // ✅ Local undo (skipRedux=true): Çizim sırasında eklenen tek socket - cache kullan
    if (skipRedux) {
      
      super.undo(scene, dispatch, skipRedux);
      return;
    }

    // ✅ Global undo (skipRedux=false): Enter sonrası tam wire + socketler
    // Socket wire sistemi için özel temizleme
    // sceneData'dan wire ve socket ID'lerini al
    const wireUUID = this.sceneData?.wireUUID;
    const socketIds = this.sceneData?.socketIds || [];
    const wireId = this.sceneData?.wireId;

    

    try {
      // 1. Wire line'ı temizle (UUID ile)
      if (wireUUID) {
        const wireLine = scene.getObjectByProperty('uuid', wireUUID);
        if (wireLine) {
          
          if (wireLine.geometry) wireLine.geometry.dispose();
          if (wireLine.material) wireLine.material.dispose();
          scene.remove(wireLine);
        }
      }

      // 2. Wire ID ile de dene (fallback)
      if (wireId && !wireUUID) {
        scene.traverse(obj => {
          if (obj.userData?.id === wireId || obj.userData?.feederId === wireId) {
            
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) obj.material.dispose();
            if (obj.parent) obj.parent.remove(obj);
          }
        });
      }

      // 3. Tüm socket'leri temizle
      const socketsToRemove = [];
      socketIds.forEach(socketId => {
        scene.traverse(obj => {
          if (obj.userData?.id === socketId) {
            socketsToRemove.push(obj);
          }
        });
      });

      socketsToRemove.forEach(obj => {
        
        obj.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        if (obj.parent) obj.parent.remove(obj);
      });

      // 4. Preview wire'ları temizle - SADECE bu command'a ait olanlar
      // ⚠️ CRITICAL: wireID kontrolü ile sadece bu hattın preview wire'larını sil
      const currentWireID = this.sceneData?.wireID || this.sceneData?.wireId;
      const previewWiresToRemove = [];
      scene.traverse(obj => {
        // Preview wire'lar sadece bu command'ın wireID'si ile eşleşmeli
        // ✅ FIX: committed socket wire'ın gerçek type'ı 'wireSocket' (useSocket:245).
        // Önceden 'socketWire' (ters yazım) aranıyordu → committed wire hiç silinmiyor,
        // local undo sonrası prizsiz ama buatlı socketWire sahnede kalıyordu.
        if ((obj.userData?.type === 'wireSocket' || obj.userData?.type === 'socketWire') && obj.type === 'Line' && currentWireID) {
          // SADECE id/wireID/feederId eşleşirse sil (line.userData.id = wireID)
          if (obj.userData?.wireID === currentWireID || obj.userData?.id === currentWireID ||
              (obj.userData?.feederId && obj.userData.feederId === this.sceneData?.wireId)) {
            previewWiresToRemove.push(obj);
          }
        }
        // NOT: Yeşil renkli wire kontrolü kaldırıldı - çok agresif ve diğer hatları da siliyordu
      });

      previewWiresToRemove.forEach(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        if (obj.parent) obj.parent.remove(obj);
      });

      // 5. Wire marker'ları temizle
      // ✅ wireID (büyük harfli) kullan - wireMarker'lar wireID ile eşleşir
      const wireIDForMarkers = this.sceneData?.wireID || wireId;
      const markersToRemove = [];
      scene.traverse(obj => {
        if (obj.userData?.role === 'wireMarker' && obj.userData?.parentWireId === wireIDForMarkers) {
          markersToRemove.push(obj);
        }
      });

      markersToRemove.forEach(obj => {
        
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        if (obj.parent) obj.parent.remove(obj);
      });

      // 6. Buat'ları temizle
      // ✅ wireID eşleşmesi VEYA buatInfos id eşleşmesi: branch (ek) buat'ın wireID'si
      // parent hattınki olduğu için wireID ile yakalanamıyordu → id ile de sil.
      const buatIdSet = new Set((this.sceneData?.buatInfos || []).map(bi => bi?.id).filter(Boolean));
      const buatsToRemove = [];
      scene.traverse(obj => {
        if (obj.userData?.type === 'socketBuat' || obj.userData?.type === 'buatCircle') {
          if (obj.userData?.wireID === this.sceneData?.wireID ||
              (obj.userData?.id && buatIdSet.has(obj.userData.id))) {
            buatsToRemove.push(obj);
          }
        }
      });

      buatsToRemove.forEach(obj => {

        obj.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        if (obj.parent) obj.parent.remove(obj);
      });

    } catch (cleanupErr) {
      console.error('[SocketCommand] Sahne temizleme hatası:', cleanupErr);
    }

    // super.undo() yerine sadece Redux temizleme yap (sahne zaten temizlendi)
    // super.undo(scene, dispatch, skipRedux);

    // Defensive cleanup: in some flows (legacy) the socket symbol or wire
    // may have been added outside the command execution path. Ensure we
    // remove any lingering scene objects matching our feeder/component ids.
    try {
      const feederId = this.data.after?.feeder?.id || this.data.before?.feeder?.id;
      const compId = this.data.after?.component?.id || this.data.before?.component?.id;

      // Try cache first (SceneObjectCache is used elsewhere)
      try {
        // dynamic import to avoid circular deps in some bundlers
        // eslint-disable-next-line global-require
        const sceneObjectCache = require('../SceneObjectCache').default;
        if (feederId) {
          const wire = sceneObjectCache.getFeeder(feederId) || sceneObjectCache.getWire?.(feederId) || sceneObjectCache.getWire?.(feederId);
          if (wire && wire.parent) {
             wire.parent.remove(wire);
            if (wire.geometry) wire.geometry.dispose();
            if (wire.material) wire.material.dispose();
          }
          sceneObjectCache.removeFeeder(feederId);
          sceneObjectCache.removeWire?.(feederId);
        }

        if (compId) {
          const comp = sceneObjectCache.getSocket(compId) || sceneObjectCache.getFixture?.(compId) || sceneObjectCache.getMachine?.(compId);
          if (comp && comp.parent) {
             comp.parent.remove(comp);
            comp.traverse(child => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) child.material.dispose();
            });
          }
          sceneObjectCache.removeSocket(compId);
        }
      } catch (cacheErr) {
        // Fallback: traverse scene and remove by matching userData ids
         if (feederId) {
          scene.traverse((obj) => {
            if (obj.userData?.id === feederId || obj.userData?.feederId === feederId || obj.userData?.lineId === feederId) {
              try {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
                if (obj.parent) obj.parent.remove(obj);
               } catch (err) {
                console.error('[SocketCommand] error removing legacy feeder', err);
              }
            }
          });
        }

        if (compId) {
          scene.traverse((obj) => {
            if (obj.userData?.id === compId || obj.userData?.socketLoadId === compId) {
              try {
                obj.traverse(child => {
                  if (child.geometry) child.geometry.dispose();
                  if (child.material) child.material.dispose();
                });
                if (obj.parent) obj.parent.remove(obj);
               } catch (err) {
                console.error('[SocketCommand] error removing legacy component', err);
              }
            }
          });
        }
      }
    } catch (cleanupErr) {
      console.error('[SocketCommand] defensive cleanup failed', cleanupErr);
    }
    // Remove Redux entries as part of global undo so sockets/feeders/wires are not
    // re-created from persistent state when restoreCallback re-enters draw mode.
    if (!skipRedux) {
      try {
        const feederId = this.data.after?.feeder?.id || this.data.before?.feeder?.id;
        const compId = this.data.after?.component?.id || this.data.before?.component?.id;
        const wireId = this.sceneData?.wireId || this.sceneData?.wireID || null;

        if (feederId) {
          try { dispatch(removeFeeder(feederId)); } catch (e) { console.warn('[SocketCommand] removeFeeder failed', e); }
        }
        if (compId) {
          try { dispatch(removeComponent(compId)); } catch (e) { console.warn('[SocketCommand] removeComponent failed', e); }
        }
        if (wireId) {
          try { dispatch(deleteWire(wireId)); } catch (e) { console.warn('[SocketCommand] deleteWire failed', e); }
        }

        // 🆕 tesisatSlice'tan tüm socket meta kayıtlarını sil
        try {
          const socketIds = this.sceneData?.socketIds || [];
          socketIds.forEach(sid => {
            if (!sid) return;
            dispatch(deleteTesisat(sid));
            // Phase A.2: floor plan indexinden de çıkar
            dispatch(removeTesisatIdFromFloorPlan({
              tabId: 'elektrik',
              floorPlanId: null, // tüm planlardan ara-sil (defansif)
              itemId: sid,
            }));
          });
        } catch (e) {
          console.warn('[SocketCommand] deleteTesisat failed', e);
        }
      } catch (err) {
        console.warn('[SocketCommand] Redux cleanup during undo failed', err);
      }
    }

    // ✅ Restore callback: re-enter draw mode with vertices and socket info
    const restoreFn = (typeof this.restoreCallback === 'function')
      ? this.restoreCallback
      : (this.sceneData && typeof this.sceneData.restoreCallback === 'function')
        ? this.sceneData.restoreCallback
        : (this.options && typeof this.options.restoreCallback === 'function')
          ? this.options.restoreCallback
          : null;

    if (typeof restoreFn === 'function') {
      try {
        const verts = this.restoreVertices || this.sceneData?.restoreVertices || this.sceneData?.wireVertices || [];
        const source = this.restoreSource || this.sceneData?.restoreSource || null;

        // ✅ isRedoContext = false (UNDO) - useBaseWiring'de exit point eklenmemeli
        restoreFn(verts, source, false, this.sceneData);
      } catch (err) {
        console.error('[SocketCommand] restoreCallback threw', err);
      }
    }
  }

  /**
   * JSON'dan SocketCommand oluştur
   */
  static fromJSON(json) {
    const action = json.type === 'ADD_SOCKET' ? 'ADD' : 'DELETE';
    const { component, feeder } = json.data.after || json.data.before;

    // Legacy support: 'socket' key yerine 'component' key kullanılmış olabilir
    const socketData = component || (json.data.after || json.data.before).socket;

    const cmd = new SocketCommand(action, socketData, feeder, json.sceneData || {});
    // restore metadata
    cmd.restoreVertices = json.restoreVertices || (json.sceneData && json.sceneData.restoreVertices) || [];
    cmd.restoreSource = (json.sceneData && json.sceneData.restoreSource) || null;
    cmd.restoreCommandType = json.restoreCommandType || (json.sceneData && json.sceneData.restoreCommandType) || null;
    return cmd;
  }

  toJSON() {
    const base = super.toJSON();
    return {
      ...base,
      restoreVertices: this.restoreVertices || [],
      restoreSource: this.restoreSource || null,
      restoreCommandType: this.restoreCommandType || null
    };
  }
}

export default SocketCommand;
